// Item pictures for a set of catalog items (2026-09-22).
// One batched call per ≤200 ids, each id asked for once. The presigned URLs the server hands back
// expire after 15 minutes, so an <img> that fails asks for exactly one refetch of that id.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CatalogPicture, catalogPictures } from '../api';

const CHUNK = 200;

export interface UseItemPicturesResult {
  /** Only items that currently have a picture appear here. */
  pictures: Record<number, CatalogPicture>;
  /** Called from an <img> onError: re-signs that id once. */
  reload: (id: number) => void;
}

export function useItemPictures(ids: Array<number | null | undefined>): UseItemPicturesResult {
  const [pictures, setPictures] = useState<Record<number, CatalogPicture>>({});
  const askedRef = useRef<Set<number>>(new Set());
  const retriedRef = useRef<Set<number>>(new Set());

  // A stable key: re-running on every parent render would re-ask for nothing.
  const wanted = useMemo(() => {
    const seen = new Set<number>();
    for (const id of ids) if (typeof id === 'number' && Number.isFinite(id) && id > 0) seen.add(id);
    return Array.from(seen).sort((a, b) => a - b);
  }, [ids]);
  const key = wanted.join(',');

  const fetchIds = useCallback((list: number[]) => {
    if (list.length === 0) return;
    const chunks: number[][] = [];
    for (let i = 0; i < list.length; i += CHUNK) chunks.push(list.slice(i, i + CHUNK));
    chunks.forEach((chunk) => {
      catalogPictures(chunk)
        .then((res) => {
          if (res && typeof res === 'object') setPictures((prev) => ({ ...prev, ...res }));
        })
        .catch(() => {
          // No catalog access, or the endpoint is not deployed yet: the screens simply show placeholders.
          chunk.forEach((id) => askedRef.current.delete(id));
        });
    });
  }, []);

  useEffect(() => {
    const todo = wanted.filter((id) => !askedRef.current.has(id));
    if (todo.length === 0) return;
    todo.forEach((id) => askedRef.current.add(id));
    fetchIds(todo);
    // `key` is the value that actually changes; `wanted` is derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, fetchIds]);

  const reload = useCallback(
    (id: number) => {
      if (!Number.isFinite(id) || retriedRef.current.has(id)) return;
      retriedRef.current.add(id);
      askedRef.current.add(id);
      fetchIds([id]);
    },
    [fetchIds]
  );

  return { pictures, reload };
}

export default useItemPictures;
